import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { CircleNotch, ArrowUpRight } from "@phosphor-icons/react";
import { login, getMe } from "@/lib/api";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [alreadyAuth, setAlreadyAuth] = useState(false);

  useEffect(() => {
    getMe()
      .then((r) => {
        if (r.authenticated) setAlreadyAuth(true);
      })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  if (alreadyAuth) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(err.message || "Errore di login");
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <CircleNotch size={32} weight="bold" className="animate-spin text-[#EB0D0D]" />
      </div>
    );
  }

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-5 py-12"
    >
      <div className="w-full max-w-md">
        <div className="mb-8">
          <div className="overline mb-3">Area riservata</div>
          <h1 className="font-display text-5xl tracking-tighter font-medium">
            Accedi al <em className="italic font-light text-[#EB0D0D]">pannello</em>.
          </h1>
          <p className="text-[#5C5A55] mt-3 text-sm">
            Gestisci le richieste ricevute dal sito.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          autoComplete="off"
          className="bg-[#F0EFEB] p-8 md:p-10 border border-[#D1CFC7]/40 space-y-5"
        >
          <div>
            <label
              htmlFor="email"
              className="block text-xs uppercase tracking-[0.15em] font-bold mb-2 text-[#1A1918]"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              data-testid="admin-login-email"
              className="w-full bg-[#FAF9F6] border border-[#D1CFC7] px-4 py-3.5 text-base focus:outline-none focus:border-[#EB0D0D]"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-xs uppercase tracking-[0.15em] font-bold mb-2 text-[#1A1918]"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-login-password"
              className="w-full bg-[#FAF9F6] border border-[#D1CFC7] px-4 py-3.5 text-base focus:outline-none focus:border-[#EB0D0D]"
            />
          </div>

          {error && (
            <div
              className="p-3 bg-[#EB0D0D]/8 text-[#C00A0A] text-sm border border-[#EB0D0D]/30"
              data-testid="admin-login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="btn-primary w-full justify-center disabled:opacity-50"
          >
            {loading ? (
              <>
                <CircleNotch size={16} weight="bold" className="animate-spin" />
                Accesso in corso…
              </>
            ) : (
              <>
                Accedi
                <ArrowUpRight size={16} weight="bold" />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
